/**
 * Org-scoped routing policy (single source of truth).
 *
 * URLs and navigation are org-scoped (`/org/:orgId/...`) for two kinds of users:
 *  - view-all admins (GLOBAL_VIEWALLORGS) — already supported historically
 *  - multi-org users (roles across more than one org) — needed so the org of the
 *    case being viewed is carried in the URL and survives a hard refresh, instead
 *    of falling back to the user's default org (see UDC multi-tenant org_id fix).
 */

// org_id can arrive as a string or a number in JWT claims; deriveIsMultiOrg
// normalizes with String(), so accept both.
type OrgRole = { org_id: string | number }

export const deriveIsMultiOrg = (authorizedRoles?: OrgRole[]): boolean =>
  new Set((authorizedRoles ?? []).map((role) => String(role.org_id))).size > 1

export const isOrgScopedRouting = (user?: {
  isAllowToViewAllOrg?: boolean
  isMultiOrg?: boolean
}): boolean => Boolean(user?.isAllowToViewAllOrg || user?.isMultiOrg)

export type OrgScopeUser = {
  isAllowToViewAllOrg?: boolean
  isMultiOrg?: boolean
  ulab_orgId?: string
}

/**
 * Multi-org (non view-all) users keep FLAT URLs for their primary org
 * (ulab_orgId) and only carry `/org/:orgId/` when viewing another org. View-all
 * admins always stay org-scoped. Single-org users are never org-scoped.
 */
export const orgUrlPrefix = (
  user: OrgScopeUser | undefined,
  targetOrgId?: string,
): string => {
  if (!isOrgScopedRouting(user) || !targetOrgId) return ""
  if (
    user?.isMultiOrg &&
    !user?.isAllowToViewAllOrg &&
    String(targetOrgId) === String(user?.ulab_orgId)
  ) {
    return ""
  }
  return `/org/${targetOrgId}`
}

/**
 * Remove a leading `/org/:orgId` segment so flat route patterns can be matched
 * against the pathname regardless of whether the URL is org-scoped or not.
 */
export const stripOrgPrefix = (pathname: string): string =>
  pathname.replace(/^\/org\/[^/]+/, "")
